import React, { FC, useCallback, useEffect } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FamilyMemberFormType } from "@utils/patientFormHelper";
import FamilyMemberForm from "@components/FamilyMemberForm/FamilyMemberForm";
import HomeFormButtons from "@components/Home/HomeFormButtons/HomeFormButtons";
import { useForm } from "react-hook-form";
import { Patient } from "@models/patient";
import { setMessage } from "@store/snackbarSlice";
import { useUpdatePatientMutation } from "@store/patientApi";
import { useDispatch } from "react-redux";
import TextIconButton from "@components/TextIconButton";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import StyledDrawer from "@components/StyledDrawer";

const FamilyMemberFormDrawer: FC<{
  open: boolean;
  patient: Patient;
  familyMember: FamilyMemberFormType | null;
  onClose: () => void;
}> = ({ open, onClose, familyMember, patient }) => {
  const { t } = useTranslation();
  const [updatePatient] = useUpdatePatientMutation();
  const dispatch = useDispatch();
  const form = useForm<FamilyMemberFormType>();
  const {
    reset,
    handleSubmit,
    clearErrors,
    formState: { isSubmitting },
  } = form;

  const resetForm = useCallback(() => {
    reset({
      id: undefined,
      nickname: "",
      firstName: "",
      middleName: "",
      lastName: "",
      gender: "Male",
      birthDate: null,
      birthLocation: "",
      bio: "",
      hobbies: "",
      relation: "",
      relationTo: "",
      occupations: null,
      residencies: null,
    });
  }, [reset]);

  useEffect(() => {
    if (familyMember) {
      reset(familyMember);
    } else {
      resetForm();
    }
  }, [familyMember, reset, resetForm]);

  const save = async (values: FamilyMemberFormType): Promise<void> => {
    try {
      const { data, error } = await updatePatient({
        id: patient.id,
        familyMembers: [
          ...(patient.familyMembers?.filter(
            (fm) => fm.id !== familyMember?.id,
          ) || []),
          { ...values },
        ],
      } as Patient);
      if (data && !error) {
        dispatch(
          setMessage({
            content: t(`familyMembers.updateSuccessMessage`),
            severity: "success",
          }),
        );
        resetForm();
        onClose();
      } else {
        dispatch(
          setMessage({ content: t(`errors.unknown`), severity: "error" }),
        );
      }
    } catch (e) {
      dispatch(setMessage({ content: t(`errors.unknown`), severity: "error" }));
    }
  };

  return (
    <StyledDrawer onClose={onClose} open={open} width="600px">
      <form
        onSubmit={(e) => {
          clearErrors();
          return handleSubmit(save)(e);
        }}
      >
        <Box flex="1">
          <FamilyMemberForm form={form} />
        </Box>

        <HomeFormButtons>
          <TextIconButton
            type="submit"
            disabled={isSubmitting}
            variant="contained"
            size="large"
            text={t(`familyMembers.dialog.saveButton`)}
            icon={SaveOutlinedIcon}
          />
        </HomeFormButtons>
      </form>
    </StyledDrawer>
  );
};

export default FamilyMemberFormDrawer;
